// frontend/src/pages/booking/admin/AdminRegister.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { bookingApi } from '../../../utils/bookingApi';
import RegisterCoaches from '../../../components/sessions/RegisterCoaches';
import { useAuth } from '../../../contexts/AuthContext';
import { PageHeader, Skeleton, ErrorState } from '../../../components/ui';
import { formatDate, formatDayDate, formatTime } from '../../../utils/dates';
import '../booking-shared.css';
import './AdminRegister.css';
import { typeName, typeColour } from '../../../utils/sessionTypes';

const STATUS_CYCLE = { UNMARKED: 'PRESENT', PRESENT: 'ABSENT', ABSENT: 'UNMARKED' };
const STATUS_LABEL = { PRESENT: 'Present', ABSENT: 'Absent', UNMARKED: 'Unmarked' };
const BACK = { to: '/admin-hub', label: 'Admin' };

function attendeeKey(a) {
  return `${a.sessionInstanceId}:${a.gymnastId}`;
}

function waitlistStatusLabel(w) {
  if (w.status !== 'OFFERED') return 'Waiting';
  if (w.offerType === 'OPEN') return 'Open offer';
  return `Offered until ${formatTime(w.offerExpiresAt)}`;
}

// A combined register's gymnasts under one heading per session, the session
// whose register was opened first. Two sessions of the same type are told
// apart by number: "Trampoline", "Trampoline (2)".
export function groupBySession(attendees, sessions, openedId) {
  const ordered = [...sessions].sort((a, b) => (a.id === openedId ? -1 : b.id === openedId ? 1 : 0));
  const seen = {};
  return ordered.map(s => {
    const base = typeName(s);
    seen[base] = (seen[base] || 0) + 1;
    return {
      id: s.id,
      colour: typeColour(s),
      label: seen[base] > 1 ? `${base} (${seen[base]})` : base,
      attendees: attendees.filter(a => a.sessionInstanceId === s.id),
    };
  });
}

export default function AdminRegister() {
  const { instanceId } = useParams();
  const [session, setSession] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [attendees, setAttendees] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [standingWaitlist, setStandingWaitlist] = useState([]);
  const [coaches, setCoaches] = useState(null); // per session, when the club uses Session coaches
  // Changing who coached a session that has ended needs session setup
  const { can } = useAuth();
  const canChangePastCoaches = can('sessionSetup', 'MANAGE');
  const [saving, setSaving] = useState({});
  const [markError, setMarkError] = useState(null);
  const [error, setError] = useState(null); // 'missing' or 'failed'
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    bookingApi.getAttendance(instanceId)
      .then(res => {
        setSession(res.data.session);
        setSessions(res.data.sessions || []);
        setAttendees(res.data.attendees);
        setWaitlist(res.data.waitlist || []);
        setStandingWaitlist(res.data.standingWaitlist || []);
        setCoaches(res.data.coaches || null);
      })
      .catch(err => setError(err.response?.status === 404 ? 'missing' : 'failed'))
      .finally(() => setLoading(false));
  }, [instanceId]);
  useEffect(() => { load(); }, [load]);

  // After changing a date's coaches: the coaches again (weekly ones when sent back)
  const reloadCoaches = useCallback(async () => {
    const res = await bookingApi.getAttendance(instanceId);
    setCoaches(res.data.coaches || null);
  }, [instanceId]);

  const handleTap = useCallback(async (gymnast) => {
    const next = STATUS_CYCLE[gymnast.status];
    const key = attendeeKey(gymnast);
    setMarkError(null);
    setAttendees(prev => prev.map(a => attendeeKey(a) === key ? { ...a, status: next } : a));
    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      if (next === 'UNMARKED') {
        await bookingApi.deleteAttendance(gymnast.sessionInstanceId, gymnast.gymnastId);
      } else {
        await bookingApi.createAttendance(gymnast.sessionInstanceId, { gymnastId: gymnast.gymnastId, status: next });
      }
    } catch {
      setAttendees(prev => prev.map(a => attendeeKey(a) === key ? { ...a, status: gymnast.status } : a));
      setMarkError(`Couldn't save ${gymnast.firstName}'s mark. Tap their name to try again.`);
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  }, []);

  const renderAttendee = (a) => {
    const key = attendeeKey(a);
    return (
      <li key={key}>
        <button
          type="button"
          className={`register-row register-row--${a.status.toLowerCase()}`}
          aria-busy={saving[key] ? 'true' : undefined}
          onClick={() => !saving[key] && handleTap(a)}
        >
          <span className="register-row__who">
            <span className="register-row__name">{a.firstName} {a.lastName}</span>
            {a.isNew && <span className="bk-pill bk-pill--info">New</span>}
          </span>
          <span className="register-row__status">
            {saving[key] ? 'Saving…' : STATUS_LABEL[a.status]}
          </span>
        </button>
      </li>
    );
  };

  const presentCount = attendees.filter(a => a.status === 'PRESENT').length;
  const absentCount = attendees.filter(a => a.status === 'ABSENT').length;
  const unmarkedCount = attendees.filter(a => a.status === 'UNMARKED').length;

  if (loading) {
    return (
      <div className="bk-page bk-page--narrow">
        <PageHeader back={BACK} title="Register" />
        <Skeleton rows={5} label="Loading register…" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bk-page bk-page--narrow">
        <PageHeader back={BACK} title="Register" />
        {error === 'missing'
          ? <ErrorState title="Couldn't find this session" text="It may have been deleted. Open it again from the sessions calendar." />
          : <ErrorState title="Couldn't load the register" onRetry={load} />}
      </div>
    );
  }

  const details = [formatDayDate(session?.date), sessions.length > 1 && [...new Set(sessions.map(typeName))].join(' + ')]
    .filter(Boolean).join(' · ');

  return (
    <div className="bk-page bk-page--narrow">
      <PageHeader back={BACK} title={`${session?.startTime} to ${session?.endTime}`} details={details} />

      {coaches && <RegisterCoaches coaches={coaches} onChanged={reloadCoaches} canChangePast={canChangePastCoaches} />}

      {/* Summary counts */}
      <div className="register-counts">
        <span className="register-counts__present">{presentCount} present</span>
        <span className="register-counts__absent">{absentCount} absent</span>
        <span className="register-counts__unmarked">{unmarkedCount} unmarked</span>
      </div>

      {markError && <p className="bk-form-error" role="alert">{markError}</p>}

      {/* Attendee list, one group per session on a combined register */}
      {attendees.length === 0 ? (
        <p className="bk-muted">No one is expected at this session.</p>
      ) : sessions.length > 1 ? (
        groupBySession(attendees, sessions, instanceId).map(group => (
          <section key={group.id} aria-label={group.label}>
            <h2 className="register-section-title">
              <span className="register-section-title__dot" style={{ background: group.colour }} aria-hidden="true" />
              {group.label}
              <span className="register-section-title__count">{group.attendees.length}</span>
            </h2>
            {group.attendees.length === 0
              ? <p className="bk-muted" style={{ margin: 0 }}>No one is expected.</p>
              : <ul className="register-list">{group.attendees.map(renderAttendee)}</ul>}
          </section>
        ))
      ) : (
        <ul className="register-list">{attendees.map(renderAttendee)}</ul>
      )}

      {waitlist.length > 0 && (
        <section>
          <h2 className="register-section-title">Waitlist for this session ({waitlist.length})</h2>
          <ol className="register-wait">
            {waitlist.map((w, i) => (
              <li key={w.id} className="register-wait__row">
                <span style={{ minWidth: 0 }}>
                  <span className="register-wait__name">{i + 1}. {w.parentName}</span>
                  {sessions.length > 1 && ` · ${typeName(w)}`}
                  {w.children.length > 0 && (
                    <span className="register-wait__children">{w.children.map(c => c.firstName).join(', ')}</span>
                  )}
                </span>
                <span className="register-wait__side">
                  {waitlistStatusLabel(w)}
                  <span>joined {formatDate(w.joinedAt)}</span>
                </span>
              </li>
            ))}
          </ol>
        </section>
      )}

      {standingWaitlist.length > 0 && (
        <section>
          <h2 className="register-section-title">Waiting for a regular place ({standingWaitlist.length})</h2>
          <ol className="register-wait">
            {standingWaitlist.map((s, i) => (
              <li key={s.commitmentId} className="register-wait__row">
                <span className="register-wait__name" style={{ minWidth: 0 }}>
                  {i + 1}. {s.firstName} {s.lastName}
                  {sessions.length > 1 && <span style={{ fontWeight: 400 }}> · {typeName(s)}</span>}
                </span>
                <span className="register-wait__side">since {formatDate(s.joinedAt)}</span>
              </li>
            ))}
          </ol>
        </section>
      )}
    </div>
  );
}
